import { type ComponentType, useCallback, useMemo, useState } from 'react';

import { type IntlShape } from 'react-intl-next';

import { type ExtensionProvider } from '@atlaskit/editor-common/extensions';
// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports
import { type EditorActions } from '@atlaskit/editor-core';
import { fg } from '@atlaskit/platform-feature-flags';
import { ArticleSearchComponent, HelpObjectSearchComponent } from '@atlassian/article-search';
import {
	type CustomSitesExtensionProviderProps,
	ExtensionMode,
	getHubDefaultParameters,
	type ProductDependenciesProviderProps,
} from '@atlassian/company-hub-common';
import { JSMHelpExtensionProvider } from '@atlassian/jsm-help-extension';
import { resourceTilesExtensionProvider } from '@atlassian/knowledge-cards-extension';
import { calloutBannerExtensionProvider } from '@atlassian/page-topper-extension';

export interface ProductExtensionsProps {
	contentId: string;
	intl: IntlShape;
	product: string;
	PropsProvider: ComponentType<ProductDependenciesProviderProps>;
}

export interface EditorConfigurationProps {
	editorActions: EditorActions;
	openConfigPanel?: (localId: string) => void;
}

export interface UseProductExtensionsReturn {
	extensionProviders: ExtensionProvider[];
	isLoading: boolean;
	loadExtensionProviders: (editorConfigurationProps: EditorConfigurationProps) => Promise<void>;
}

const getCSMExtensions = (
	defaultExtensionProviderProps: any,
	contentId: string,
	PropsProvider: any,
) => {
	const extensions = [];

	// Add callout-banner extension factory
	extensions.push(
		calloutBannerExtensionProvider({
			...defaultExtensionProviderProps,
			CustomSearchComponent: ArticleSearchComponent,
		}),
	);

	// Add resource tiles extension factory
	extensions.push(
		resourceTilesExtensionProvider({
			contentId,
			propsProvider: PropsProvider,
			isInEditor: true,
		}),
	);

	return extensions;
};

const getJSMExtensions = (
	defaultExtensionProviderProps: any,
	contentId: string,
	PropsProvider: any,
) => {
	const extensions = [];

	extensions.push(
		calloutBannerExtensionProvider({
			...defaultExtensionProviderProps,
			CustomSearchComponent: HelpObjectSearchComponent,
		}),
	);

	extensions.push(
		JSMHelpExtensionProvider({
			...defaultExtensionProviderProps,
			contentId,
			propsProvider: PropsProvider,
			mode: ExtensionMode.EDITOR,
		}),
	);

	return extensions;
};

export const useProductExtensions = (props: ProductExtensionsProps): UseProductExtensionsReturn => {
	const { contentId, intl, PropsProvider, product } = props;

	// State to track extension providers
	const [extensionProviders, setExtensionProviders] = useState<ExtensionProvider[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	// Memoized extension provider props
	const defaultExtensionProviderProps = useMemo(
		() => ({
			contentId,
			propsProvider: PropsProvider,
			mode: ExtensionMode.EDITOR,
			defaultParameters: getHubDefaultParameters(),
		}),
		[contentId, PropsProvider],
	);

	// Memoized extension factories array
	const extensionFactories = useMemo(() => {
		const factories: Array<
			Promise<(props: CustomSitesExtensionProviderProps) => Promise<ExtensionProvider<any>>>
		> = [];

		if (product === 'CUSTOMER_SERVICE') {
			factories.push(...getCSMExtensions(defaultExtensionProviderProps, contentId, PropsProvider));
		}

		if (product === 'HELP_HUB' && fg('enable-jsm-in-hubs')) {
			factories.push(...getJSMExtensions(defaultExtensionProviderProps, contentId, PropsProvider));
		}

		return factories;
	}, [defaultExtensionProviderProps, product, contentId, PropsProvider]);

	// Function to load extension providers with editor configuration
	const loadExtensionProviders = useCallback(
		async (editorConfigurationProps: EditorConfigurationProps) => {
			if (!product) {
				setExtensionProviders([]);
				return;
			}

			setIsLoading(true);

			try {
				const defaultExtensionInstanceProps = {
					editorActions: editorConfigurationProps.editorActions,
					openConfigPanel: editorConfigurationProps.openConfigPanel,
					// contentId technically not used in all providers, but required in
					// CustomSitesExtensionProviderProps; we should disentangle this later
					contentId,
					intl,
				};

				// Apply editor configuration to all extension providers concurrently
				const results = await Promise.allSettled(
					extensionFactories.map(async (factoryPromise) => {
						const providerInstance = await factoryPromise;
						return await providerInstance(defaultExtensionInstanceProps);
					}),
				);

				// Filter out failed results and return only successful extension providers
				const loadedProviders = results
					.filter((result): result is PromiseFulfilledResult<ExtensionProvider> => {
						if (result.status === 'rejected') {
							// todo: log error to analytics
							// eslint-disable-next-line no-console
							console.error('Error loading extension provider:', result.reason);
							return false;
						}
						return true;
					})
					.map((result) => result.value);

				setExtensionProviders(loadedProviders);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error loading product extensions:', error);
				setExtensionProviders([]);
			} finally {
				setIsLoading(false);
			}
		},
		[contentId, intl, product, extensionFactories],
	);

	return {
		extensionProviders,
		loadExtensionProviders,
		isLoading,
	};
};
